import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { profileAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  ArrowLeft, 
  GraduationCap, 
  Globe, 
  DollarSign, 
  Calendar, 
  BookOpen,
  Languages,
  CheckCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";

const ProfileBuilderPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    education_level: '',
    preferred_countries: [],
    budget_range: '',
    intake_date: '',
    field_of_study: '',
    english_test: '',
    english_score: '',
    work_experience_years: 0,
    additional_info: '',
    // Phase B — academic record
    matric_percentage: '',
    intermediate_percentage: '',
    bachelors_percentage: '',
    bachelors_gpa: '',
    bachelors_gpa_scale: '',
    english_test_type: '',
    english_overall_band: '',
  });

  const countries = [
    { value: 'UK', label: 'United Kingdom' },
    { value: 'USA', label: 'United States' },
    { value: 'Canada', label: 'Canada' },
    { value: 'Ireland', label: 'Ireland' },
    { value: 'France', label: 'France' },
    { value: 'Germany', label: 'Germany' },
    { value: 'Netherlands', label: 'Netherlands' }
  ];

  const educationLevels = [
    { value: 'high_school', label: 'High School / A-Levels' },
    { value: 'bachelors', label: 'Bachelor\'s Degree' },
    { value: 'masters', label: 'Master\'s Degree' },
    { value: 'phd', label: 'PhD / Doctorate' }
  ];

  const budgetRanges = [
    { value: 'low', label: 'Budget Friendly (< $15,000/year)' },
    { value: 'medium', label: 'Moderate ($15,000 - $30,000/year)' },
    { value: 'high', label: 'Premium ($30,000 - $50,000/year)' },
    { value: 'premium', label: 'No Budget Constraint (> $50,000/year)' }
  ];

  const intakeDates = [
    'September 2025',
    'January 2026',
    'May 2026',
    'September 2026',
    'January 2027'
  ];

  const fieldsOfStudy = [
    'Computer Science',
    'Business Administration',
    'Engineering',
    'Medicine & Healthcare',
    'Law',
    'Arts & Humanities',
    'Data Science & AI',
    'Finance & Economics',
    'Psychology',
    'Architecture',
    'Environmental Science',
    'Other'
  ];

  const englishTests = [
    { value: 'IELTS', label: 'IELTS' },
    { value: 'OXFORD_ELLT', label: 'Oxford ELLT' },
    { value: 'PTE', label: 'Pearson PTE Academic' },
    { value: 'TOEFL', label: 'TOEFL iBT' },
    { value: 'DUOLINGO', label: 'Duolingo English Test (DET)' },
    { value: 'NONE', label: 'Not taken yet' }
  ];

  const toggleCountry = (country) => {
    setFormData(prev => ({
      ...prev,
      preferred_countries: prev.preferred_countries.includes(country)
        ? prev.preferred_countries.filter(c => c !== country)
        : [...prev.preferred_countries, country]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
      const payload = {
        ...formData,
        matric_percentage: numOrNull(formData.matric_percentage),
        intermediate_percentage: numOrNull(formData.intermediate_percentage),
        bachelors_percentage: numOrNull(formData.bachelors_percentage),
        bachelors_gpa: numOrNull(formData.bachelors_gpa),
        bachelors_gpa_scale: numOrNull(formData.bachelors_gpa_scale),
        english_overall_band: numOrNull(formData.english_overall_band || formData.english_score),
      };
      await profileAPI.create(payload);
      toast.success('Profile created successfully!');
      navigate('/student');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.education_level && formData.field_of_study;
      case 2:
        return formData.preferred_countries.length > 0;
      case 3:
        return formData.budget_range && formData.intake_date;
      case 4:
        return formData.english_test;
      default:
        return true;
    }
  };

  const stepConfig = [
    { icon: GraduationCap, title: 'Education', desc: 'Your academic background' },
    { icon: Globe, title: 'Destinations', desc: 'Where you want to study' },
    { icon: Calendar, title: 'Timeline', desc: 'Budget & intake preferences' },
    { icon: Languages, title: 'English Test', desc: 'Language proficiency' }
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 py-12 px-4 transition-colors">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="H E Direct" className="h-12 mx-auto mb-6" />
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-white mb-2">Build Your Profile</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Hi {user?.full_name?.split(' ')[0]}! Let&apos;s find the perfect courses for you.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {stepConfig.map((config, idx) => (
            <div key={idx} className="flex-1 flex items-center">
              <div className="flex flex-col items-center">
                <div className={`step-indicator ${step > idx + 1 ? 'completed' : step === idx + 1 ? 'active' : 'pending'}`}>
                  {step > idx + 1 ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <config.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-xs mt-2 ${step === idx + 1 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {config.title}
                </span>
              </div>
              {idx < stepConfig.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step > idx + 1 ? 'bg-green-500' : 'bg-gray-200 dark:bg-slate-600'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-8">
          {/* Step 1: Education */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-teal-600/10 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Education Background</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Tell us about your academic journey</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Highest Education Level *</Label>
                <Select 
                  value={formData.education_level} 
                  onValueChange={(value) => setFormData({ ...formData, education_level: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 h-12" data-testid="profile-education">
                    <SelectValue placeholder="Select your education level" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    {educationLevels.map((level) => (
                      <SelectItem key={level.value} value={level.value} className="text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600">
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Field of Study *</Label>
                <Select 
                  value={formData.field_of_study} 
                  onValueChange={(value) => setFormData({ ...formData, field_of_study: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 h-12" data-testid="profile-field">
                    <SelectValue placeholder="Select your field of interest" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    {fieldsOfStudy.map((field) => (
                      <SelectItem key={field} value={field} className="text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600">
                        {field}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Work Experience (Years)</Label>
                <Select 
                  value={formData.work_experience_years.toString()} 
                  onValueChange={(value) => setFormData({ ...formData, work_experience_years: parseInt(value) })}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 h-12" data-testid="profile-experience">
                    <SelectValue placeholder="Select years of experience" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                      <SelectItem key={year} value={year.toString()} className="text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600">
                        {year === 0 ? 'Fresh graduate / No experience' : year === 10 ? '10+ years' : `${year} years`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Matric / O-Level %</Label>
                  <Input
                    type="number" min="0" max="100" step="0.1"
                    placeholder="e.g. 85"
                    value={formData.matric_percentage}
                    onChange={(e) => setFormData({ ...formData, matric_percentage: e.target.value })}
                    className="h-11"
                    data-testid="profile-matric"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Intermediate / A-Level %</Label>
                  <Input
                    type="number" min="0" max="100" step="0.1"
                    placeholder="e.g. 78"
                    value={formData.intermediate_percentage}
                    onChange={(e) => setFormData({ ...formData, intermediate_percentage: e.target.value })}
                    className="h-11"
                    data-testid="profile-intermediate"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Bachelor&apos;s %</Label>
                  <Input
                    type="number" min="0" max="100" step="0.1"
                    placeholder="e.g. 65"
                    value={formData.bachelors_percentage}
                    onChange={(e) => setFormData({ ...formData, bachelors_percentage: e.target.value })}
                    className="h-11"
                    data-testid="profile-bachelors"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Bachelor&apos;s GPA (optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number" min="0" step="0.01"
                      placeholder="3.4"
                      value={formData.bachelors_gpa}
                      onChange={(e) => setFormData({ ...formData, bachelors_gpa: e.target.value })}
                      className="h-11"
                      data-testid="profile-gpa"
                    />
                    <Input
                      type="number" min="0" step="0.1"
                      placeholder="/4.0"
                      value={formData.bachelors_gpa_scale}
                      onChange={(e) => setFormData({ ...formData, bachelors_gpa_scale: e.target.value })}
                      className="h-11 w-20"
                      data-testid="profile-gpa-scale"
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 italic">Adding your academic percentages enables more accurate course recommendations.</p>
            </div>
          )}

          {/* Step 2: Destinations */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-teal-600/10 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Preferred Destinations</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Select countries where you&apos;d like to study (select multiple)</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {countries.map((country) => (
                  <div
                    key={country.value}
                    onClick={() => toggleCountry(country.value)}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.preferred_countries.includes(country.value)
                        ? 'border-teal-500 bg-teal-600/10'
                        : 'border-gray-200 dark:border-slate-600 hover:border-teal-400 dark:hover:border-teal-500 bg-white dark:bg-slate-700'
                    }`}
                    data-testid={`country-${country.value}`}
                  >
                    <Checkbox
                      checked={formData.preferred_countries.includes(country.value)}
                      className="data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-500"
                    />
                    <span className="text-gray-700 dark:text-gray-200">{country.label}</span>
                  </div>
                ))}
              </div>
              
              {formData.preferred_countries.length > 0 && (
                <p className="text-sm text-teal-600 dark:text-teal-400">
                  Selected: {formData.preferred_countries.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Step 3: Budget & Timeline */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-teal-600/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Budget & Timeline</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Help us find courses within your budget</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Annual Budget Range *</Label>
                <Select 
                  value={formData.budget_range} 
                  onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 h-12" data-testid="profile-budget">
                    <SelectValue placeholder="Select your budget range" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    {budgetRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value} className="text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600">
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Preferred Intake Date *</Label>
                <Select 
                  value={formData.intake_date} 
                  onValueChange={(value) => setFormData({ ...formData, intake_date: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 h-12" data-testid="profile-intake">
                    <SelectValue placeholder="When do you want to start?" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    {intakeDates.map((date) => (
                      <SelectItem key={date} value={date} className="text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600">
                        {date}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 4: English Test */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-teal-600/10 flex items-center justify-center">
                  <Languages className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">English Proficiency</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Your language test information</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">English Test Type *</Label>
                <Select 
                  value={formData.english_test} 
                  onValueChange={(value) => setFormData({ ...formData, english_test: value, english_test_type: value === 'NONE' ? '' : value })}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 h-12" data-testid="profile-english-test">
                    <SelectValue placeholder="Select your English test" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    {englishTests.map((test) => (
                      <SelectItem key={test.value} value={test.value} className="text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600">
                        {test.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.english_test && formData.english_test !== 'NONE' && (
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Your Score (Optional)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 7.0 (IELTS), 100 (TOEFL), 65 (PTE), 120 (Duolingo), 8 (Oxford ELLT)"
                    value={formData.english_score}
                    onChange={(e) => setFormData({ ...formData, english_score: e.target.value, english_overall_band: e.target.value })}
                    className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 h-12"
                    data-testid="profile-english-score"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Additional Information (Optional)</Label>
                <Textarea
                  placeholder="Any specific requirements or preferences..."
                  value={formData.additional_info}
                  onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white focus:border-teal-500 min-h-[100px]"
                  data-testid="profile-additional-info"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-slate-600">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                data-testid="profile-back"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}
            
            {step < totalSteps ? (
              <Button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                data-testid="profile-next"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                data-testid="profile-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Complete Profile
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileBuilderPage;
